import * as React from "react";
import { graphql, useMutation } from "react-relay";
import { useComlink, WorkerKey } from "@/workers";
import { isEqual, cloneDeep } from "lodash";
import type {
  Format,
  Tokens,
  InferSchemaOptions,
  Schema,
  DebugInferSchemaError,
  DirReaderOptions,
} from "@aqora-io/aqora-data-utils";
import type { UploadUpdate } from "@/workers/worker/dataUtils";
import { Context, SamplesMeta } from "./context";
import { useAuth } from "@/utils/auth";
import { commitMutationPromise } from "@/utils/relay";
import * as Comlink from "comlink";
import { ProviderStartUploadMutation as StartUploadMutationType } from "./__generated__/ProviderStartUploadMutation.graphql";
import { ProviderFinishUploadMutation as FinishUploadMutationType } from "./__generated__/ProviderFinishUploadMutation.graphql";
import { useDatasetVersionTableContext } from "@/components/DatasetVersionTable/context";

const StartUploadMutation = graphql`
  mutation ProviderStartUploadMutation($datasetVersionId: ID!) {
    startDatasetVersionUpload(datasetVersionId: $datasetVersionId) {
      id
    }
  }
`;

const FinishUploadMutation = graphql`
  mutation ProviderFinishUploadMutation($datasetVersionId: ID!) {
    finishDatasetVersionUpload(datasetVersionId: $datasetVersionId) {
      id
      ...DatasetVersionTableFragment
      ...DatasetVersionTableRecordsFragment
      ...DatasetVersionCardFragment
    }
  }
`;

const DEFAULT_MAX_PARTITION_SIZE = 1024 * 1024 * 1024; // 1GB
const DEFAULT_PART_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_RECORD_BATCH_SIZE = 1024 * 1024; // 1MB
const DEFAULT_RECORD_BATCH_RECORDS = 1024;
const calculateWriteOptions = (
  samplesMeta?: SamplesMeta,
  maxPartitionSize: number = DEFAULT_MAX_PARTITION_SIZE,
  recordBatchSize: number = DEFAULT_RECORD_BATCH_SIZE,
  partSize: number = DEFAULT_PART_SIZE,
) => {
  const averageSampleSize =
    samplesMeta && samplesMeta.samplesRead > 0
      ? Number(samplesMeta.bytesRead / BigInt(samplesMeta.samplesRead))
      : undefined;
  const readBatchSize = Math.max(
    100,
    averageSampleSize
      ? Math.floor(recordBatchSize / averageSampleSize)
      : DEFAULT_RECORD_BATCH_RECORDS,
  );
  return {
    part_size: partSize,
    max_partition_size: maxPartitionSize,
    read_batch_size: readBatchSize,
  };
};

interface ProviderProps extends React.PropsWithChildren {
  files: File[];
  datasetVersionId: string;
}

export const Provider: React.FC<ProviderProps> = ({
  files,
  datasetVersionId,
  children,
}) => {
  const auth = useAuth();
  const dataUtils = useComlink(WorkerKey.DataUtils);
  const { setPage: setDatasetVersionTablePage } =
    useDatasetVersionTableContext();

  const [editable, setEditable] = React.useState(false);
  const [format, setFormat] = React.useState<Format>();
  const [dirReaderOptions, setDirReaderOptions] =
    React.useState<DirReaderOptions>();
  const [inferSchemaOptions, setInferSchemaOptions] =
    React.useState<InferSchemaOptions>({
      sample_size: files.length > 1 ? 100 : 1000,
    });
  const inferArgsRef = React.useRef<{
    files: File[];
    format?: Format;
    dirReaderOptions?: DirReaderOptions;
    inferSchemaOptions?: InferSchemaOptions;
  }>();

  const [inferLoading, setInferLoading] = React.useState(false);
  const [inferError, setInferError] = React.useState<Error>();
  const [schema, setSchema] = React.useState<Schema>();
  const [debugInferError, setDebugInferError] =
    React.useState<DebugInferSchemaError>();
  const [recordBatchesError, setRecordBatchesError] = React.useState<Error>();
  const [samplesMeta, setSamplesMeta] = React.useState<SamplesMeta>();

  const [processedBytes, setProcessedBytes] = React.useState(BigInt(0));
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<Error>();

  const [commitStartUpload] =
    useMutation<StartUploadMutationType>(StartUploadMutation);
  const [commitFinishUpload] =
    useMutation<FinishUploadMutationType>(FinishUploadMutation);

  // Load schema with changes to file, format, or inferSchemaOptions
  React.useEffect(() => {
    if (uploading) {
      return;
    }
    const inferArgs = {
      files,
      format,
      dirReaderOptions,
      inferSchemaOptions,
    };
    if (isEqual(inferArgsRef.current, inferArgs)) {
      return;
    }
    if (inferLoading) {
      dataUtils.restart();
      setInferLoading(false);
      return;
    }
    inferArgsRef.current = cloneDeep(inferArgs);
    setInferError(undefined);
    setInferLoading(true);
    dataUtils.worker
      .infer({ files, format, dirReaderOptions }, inferSchemaOptions)
      .then(
        ({
          format,
          editable,
          dirReaderOptions,
          schema,
          debugInferError,
          recordBatchesError,
          samplesRead,
          bytesRead,
        }) => {
          setEditable(editable);
          if (format) {
            // We don't need to re-calculate because the format was inferred
            if (
              isEqual(inferArgsRef.current, inferArgs) &&
              inferArgs.format == undefined
            ) {
              inferArgsRef.current!.format = cloneDeep(format);
            }
            setFormat(format);
          }
          if (dirReaderOptions) {
            // We don't need to re-calculate because the format was inferred
            if (
              isEqual(inferArgsRef.current, inferArgs) &&
              inferArgs.dirReaderOptions == undefined
            ) {
              inferArgsRef.current!.dirReaderOptions =
                cloneDeep(dirReaderOptions);
            }
            setDirReaderOptions(dirReaderOptions);
          }
          setSamplesMeta({ samplesRead, bytesRead });
          if (schema) {
            setSchema(schema);
          }
          if (debugInferError) {
            setDebugInferError(debugInferError);
          } else {
            setDebugInferError(undefined);
          }
          if (recordBatchesError) {
            setRecordBatchesError(recordBatchesError);
          } else {
            setRecordBatchesError(undefined);
          }
        },
      )
      .catch((error) => {
        setInferError(error);
        setDebugInferError(undefined);
      })
      .finally(() => {
        setInferLoading(false);
      });
  }, [
    files,
    format,
    dirReaderOptions,
    inferSchemaOptions,
    dataUtils,
    inferLoading,
    uploading,
  ]);

  const upload = React.useCallback(
    async (onComplete: () => void) => {
      if (!schema) {
        return;
      }
      setUploading(true);
      setUploadError(undefined);
      setProcessedBytes(BigInt(0));
      const url =
        import.meta.env.VITE_API_URL || `${window.location.origin}/graphql`;
      try {
        await commitMutationPromise(commitStartUpload)({
          variables: { datasetVersionId },
        });
        await dataUtils.worker.upload(
          { files, format, dirReaderOptions },
          schema,
          url,
          datasetVersionId,
          calculateWriteOptions(samplesMeta),
          Comlink.proxy(async () => auth.getToken()),
          Comlink.proxy(async () => auth.revokeToken("access")),
          Comlink.proxy(async () => auth.revokeToken("refresh")),
          Comlink.proxy(
            async ({ access_token: access, refresh_token: refresh }: Tokens) =>
              auth.setTokens({ access, refresh }),
          ),
          Comlink.proxy((update: UploadUpdate) => {
            setProcessedBytes(update.bytes);
          }),
        );
        setDatasetVersionTablePage(0);
        await commitMutationPromise(commitFinishUpload)({
          variables: { datasetVersionId },
        });
        onComplete();
      } catch (error) {
        setUploadError(error as Error);
      } finally {
        setUploading(false);
      }
    },
    [
      files,
      format,
      dirReaderOptions,
      schema,
      auth,
      dataUtils,
      datasetVersionId,
      samplesMeta,
      setProcessedBytes,
      commitStartUpload,
      commitFinishUpload,
      setDatasetVersionTablePage,
    ],
  );

  const cancelUpload = React.useCallback(() => {
    dataUtils.restart();
    setUploading(false);
  }, [dataUtils]);

  return (
    <Context.Provider
      value={{
        files,
        editable,
        format,
        setFormat,
        dirReaderOptions,
        setDirReaderOptions,
        inferSchemaOptions,
        setInferSchemaOptions,
        inferLoading,
        inferError,
        schema,
        debugInferError,
        recordBatchesError,
        uploading,
        processedBytes,
        uploadError,
        upload,
        cancelUpload,
      }}
    >
      {children}
    </Context.Provider>
  );
};
